import React, { useState, useEffect, useCallback } from 'react'
import useGlobalCustomerStore from '../../stores/globalCustomerStore'
import useSocialMediaStore from '../../stores/socialMediaStore'
import scoutService, { ContentPost } from '../../services/scoutService'
import { socialMediaService } from '../../services/socialMediaService'
import { Cell } from './mockData'
import ContentHeatmap, { RawCell } from './ContentHeatmap'
import CategoryDetailView from './CategoryDetailView'

/**
 * Content Performance heatmap. Rendered as a tab inside the Social dashboard, so it does NOT
 * own a filter bar — it INHERITS the dashboard's filters (period/custom range, owned-partner
 * account type, platform multi-select, and any specific account selection). This guarantees the
 * heatmap's counts/EMV always agree with the dashboard KPIs (single source of truth). A specific
 * account selection takes precedence over the owned/partner scope (handled server-side).
 */
const ContentPerformancePage: React.FC = () => {
  const { selectedCustomer, getAPIFilterParams } = useGlobalCustomerStore()
  const customerId = selectedCustomer?.id ? String(selectedCustomer.id) : ''
  const { currentPeriod, customDateRange, accountType, selectedPlatformIds, selectedAccountIds } = useSocialMediaStore()

  const [raw, setRaw] = useState<RawCell[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [detail, setDetail] = useState<{ categoryKey: string; categoryLabel: string; format: string; cell: Cell } | null>(null)

  // Derive the scoutService options from the inherited dashboard filters.
  const isCustom = currentPeriod === 'custom'
  const globalAccountIds = getAPIFilterParams().accountIds
  const accountIds = globalAccountIds && globalAccountIds.length ? globalAccountIds
    : selectedAccountIds.length ? selectedAccountIds : undefined
  const platformIds = selectedPlatformIds.length ? selectedPlatformIds : undefined
  const cpOpts = {
    period: isCustom ? undefined : currentPeriod,
    customStartDate: isCustom && customDateRange ? customDateRange.start : undefined,
    customEndDate: isCustom && customDateRange ? customDateRange.end : undefined,
    accountType,
    accountIds,
    platformIds,
  }

  const load = useCallback(async () => {
    if (!customerId) return
    setLoading(true); setError(null)
    try {
      const res = await scoutService.getContentPerformance(customerId, cpOpts)
      setRaw(res?.data ?? [])
    } catch (e) {
      // Surface the server's message (BaseService throws with error.message) rather than a
      // generic string — a feature-gate 403 reads "… is not enabled for your organization",
      // which is far more actionable than "Failed to load".
      const msg = e instanceof Error && e.message ? e.message : 'Failed to load content performance'
      setError(msg); setRaw([])
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, currentPeriod, customDateRange?.start, customDateRange?.end, accountType, JSON.stringify(accountIds), JSON.stringify(platformIds)])

  useEffect(() => { load() }, [load])

  if (detail) {
    return (
      <CategoryDetailView
        categoryLabel={detail.categoryLabel}
        format={detail.format}
        cell={detail.cell}
        fetchPosts={async () => (await scoutService.getContentPerformancePosts(customerId, {
          contentCategory: detail.categoryKey, format: detail.format, ...cpOpts,
        })).data ?? []}
        fetchPostBrands={async (post: ContentPost) => post.post_id ? (await scoutService.getPostBrands(customerId, post.post_id)).data ?? [] : []}
        thumbnailUrl={(post: ContentPost) => post.thumbnail_blob_path
          ? socialMediaService.getThumbnailUrl(post.social_post_id, customerId)
          : post.post_id ? scoutService.getThumbnailUrl(post.post_id, customerId) : null}
        onBack={() => setDetail(null)}
      />
    )
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-bold text-gray-900">Value Map</h2>
        <p className="text-sm text-gray-500">Content theme × format — shaded by total EMV, reflecting the filters above. Click a cell to drill in.</p>
      </div>
      <ContentHeatmap cells={raw} loading={loading} error={error} onCellClick={(categoryKey, categoryLabel, format, cell) => setDetail({ categoryKey, categoryLabel, format, cell })} />
    </div>
  )
}

export default ContentPerformancePage
